/* ARM wholesale ordering — the Shopify page's front end.
 *
 * Holds no secret. It signs in with a store's access code, keeps the signed
 * token the server returns, and asks the server for everything else. Totals are
 * drawn with arm-b2b-pricing.js (generated from the server's own arithmetic) so
 * the figures update as you type, but the server re-prices every order it
 * receives — nothing here is trusted.
 */
(function () {
  'use strict';

  var CFG = window.ARM_B2B || {};
  var TOKEN_KEY = 'arm_b2b_token';
  var app = document.getElementById('app');

  var state = {
    token: null,
    session: null,     // { role, storeName, profile, products, shipTo, ... }
    view: 'signin',
    busy: false,
    error: '',
    quantities: {},
    form: {},
    altShipTo: null,
    copiedFrom: '',
    copyDropped: '',
    justSubmittedId: '',
    orders: null,
    order: null,
    queue: null,
    notices: [],
    rejecting: '',
    note: '',
    // Warehouse. Authenticated by a token in the link, never by a session.
    shipToken: '',
    shipOrder: null,
    shipQueue: null,
    shipForm: { carrier: '', tracking: '' },
    shipDone: null
  };

  // ── plumbing ──────────────────────────────────────────────────
  function api(action, payload) {
    var body = Object.assign({ action: action }, payload || {});
    if (state.token) body.token = state.token;

    return fetch(CFG.api, {
      method: 'POST',
      // text/plain keeps this a simple request — no preflight, which Apps
      // Script cannot answer.
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.text();
    }).then(function (text) {
      var data;
      try {
        data = JSON.parse(text);
      } catch (err) {
        throw new Error('The ordering service is not responding properly. Try again shortly.');
      }
      if (data.status === 'error') throw new Error(data.message || 'Something went wrong.');
      return data;
    }, function () {
      throw new Error('Could not reach the ordering service. Check your connection and try again.');
    });
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(n) {
    return (Number(n) || 0).toLocaleString('en-CA', {
      style: 'currency', currency: 'CAD', minimumFractionDigits: 2
    });
  }

  function when(iso) {
    if (!iso) return '—';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function todayISO() {
    var now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }

  function statusBadge(status) {
    var map = {
      pending: ['b-pending', 'Awaiting approval'],
      approved: ['b-approved', 'Approved'],
      shipped: ['b-shipped', 'Shipped'],
      rejected: ['b-rejected', 'Declined']
    };
    var s = map[String(status).toLowerCase()] || map.pending;
    return '<span class="badge ' + s[0] + '">' + s[1] + '</span>';
  }

  function currentQuote() {
    if (!state.session || !state.session.profile) return null;
    var province = (state.altShipTo || state.session.shipTo || {}).province;

    var sellable = {};
    (state.session.products || []).forEach(function (p) {
      if (p.available !== false && state.quantities[p.id]) sellable[p.id] = state.quantities[p.id];
    });
    return _quote(state.session.profile, sellable, province);
  }

  // ── routing ───────────────────────────────────────────────────
  function go(view, opts) {
    if (view !== 'detail') state.justSubmittedId = '';
    state.view = view;
    state.error = '';
    opts = opts || {};
    if (view === 'detail') {
      location.hash = '#/o/' + opts.id;
    } else {
      location.hash = '#/' + view;
    }
    render();
    if (view === 'history') loadOrders();
    if (view === 'admin') loadQueue();
    if (view === 'detail') loadOrder(opts.id);
  }

  function routeFromHash() {
    var h = (location.hash || '').replace(/^#\/?/, '');
    if (h.indexOf('ships/') === 0) return { view: 'ships', token: h.slice(6) };
    if (h.indexOf('ship/') === 0) return { view: 'ship', token: h.slice(5) };
    if (h.indexOf('o/') === 0) return { view: 'detail', id: h.slice(2) };
    if (h === 'history' || h === 'admin' || h === 'order') return { view: h };
    return null;
  }

  // ── data ──────────────────────────────────────────────────────
  function loadOrders() {
    state.orders = null;
    api('portalMyOrders').then(function (d) {
      state.orders = d.orders || [];
      render();
    }, function (e) { state.error = e.message; state.orders = []; render(); });
  }

  function loadQueue() {
    state.queue = null;
    api('portalQueue').then(function (d) {
      state.queue = d;
      render();
    }, function (e) { state.error = e.message; state.queue = { pending: [], decided: [] }; render(); });
  }

  function loadOrder(id) {
    state.order = null;
    api('portalOrder', { id: id }).then(function (d) {
      state.order = d.order || false;
      render();
    }, function (e) { state.error = e.message; state.order = false; render(); });
  }

  function loadShipOrder() {
    state.shipOrder = null; state.error = '';
    api('shipView', { token: state.shipToken }).then(function (d) {
      state.shipOrder = d.order;
      state.shipCarriers = d.carriers || [];
      state.shipForm = { carrier: d.order.carrier || '', tracking: d.order.tracking || '' };
      render();
    }, function (e) { state.error = e.message; state.shipOrder = false; render(); });
  }

  function loadShipQueue() {
    state.shipQueue = null; state.error = '';
    api('shipQueue', { token: state.shipToken }).then(function (d) {
      state.shipQueue = d.orders || [];
      render();
    }, function (e) { state.error = e.message; state.shipQueue = []; render(); });
  }

  function submitShip() {
    if (state.busy) return;
    if (!state.shipForm.carrier) { state.error = 'Choose a carrier.'; return render(); }
    if (!String(state.shipForm.tracking).trim()) {
      state.error = 'Enter the tracking number.'; return render();
    }
    state.busy = true; state.error = ''; render();

    api('shipSubmit', {
      token: state.shipToken,
      carrier: state.shipForm.carrier,
      tracking: state.shipForm.tracking
    }).then(function (d) {
      state.busy = false;
      state.shipDone = d;
      loadShipOrder();
    }, function (e) { state.busy = false; state.error = e.message; render(); });
  }

  function signOut() {
    try { localStorage.removeItem(TOKEN_KEY); } catch (e) {}
    state.token = null;
    state.session = null;
    state.quantities = {};
    state.orders = null;
    state.queue = null;
    location.hash = '';
    state.view = 'signin';
    render();
  }

  // ── views ─────────────────────────────────────────────────────
  function viewSignIn() {
    var want = routeFromHash();
    var review = !!(want && (want.view === 'admin' || want.view === 'detail'));

    return '' +
      '<div class="signin">' +
        '<div>' +
          '<div style="display:flex;align-items:baseline;gap:12px">' +
            '<span class="mark" style="color:var(--brand)">ARM</span>' +
            '<span class="kick" style="color:var(--ink-faint)">Wholesale ordering</span>' +
          '</div>' +
          '<h1 style="margin-top:24px">' +
            (review ? 'Sign in to review this order' : 'Order stock for your store') + '</h1>' +
          '<p class="sub" style="margin-top:12px;max-width:52ch">' +
            (review
              ? 'Enter your code and we will take you straight there.'
              : 'Enter the access code we issued your location. One code per store &mdash; share it with whoever places orders.') +
          '</p>' +
          '<form id="signin-form" style="margin-top:28px" novalidate>' +
            '<label class="lbl" for="code">' +
              (review ? 'Your admin code' : 'Store access code') + '</label>' +
            '<div style="display:flex;flex-direction:column;gap:12px">' +
              '<input id="code" class="field tabular" style="text-transform:uppercase;letter-spacing:.08em" ' +
                'autocomplete="one-time-code" autocapitalize="characters" spellcheck="false" ' +
                'placeholder="' + (review ? 'ARM-OWNER-XXXXXX' : 'NUTTERS-KAMLOOPS-XXXXX') + '">' +
              '<button type="submit" class="btn btn-primary" id="signin-btn"' + (state.busy ? ' disabled' : '') + '>' +
                (state.busy ? 'Checking' : 'Sign in') + '</button>' +
            '</div>' +
            (state.error ? '<p class="alert alert-err" role="alert">' + esc(state.error) + '</p>' : '') +
          '</form>' +
          (review ? '' : '<ol class="steps">' +
            '<li><span class="n">01</span><span>Build your order from the current wholesale rate card.</span></li>' +
            '<li><span class="n">02</span><span>We review it — nothing ships or invoices before that.</span></li>' +
            '<li><span class="n">03</span><span>On approval the invoice goes to you and the warehouse.</span></li>' +
          '</ol>') +
          '<p style="margin-top:24px;color:var(--ink-faint);font-size:14px">Lost your code? Email ' +
            '<a href="mailto:' + esc(CFG.support) + '"><strong>' + esc(CFG.support) + '</strong></a>.</p>' +
        '</div>' +
        '<aside class="panel lineup">' +
          '<div style="display:flex;justify-content:space-between;border-bottom:1px solid var(--rule);padding:12px 20px">' +
            '<span class="kick" style="color:var(--ink-soft)">Current line-up</span>' +
            '<span class="tabular" style="font-size:11px;color:var(--ink-faint)">3 flavours · 2 formats</span>' +
          '</div>' +
          '<div class="lineup-grid">' +
            lineupCell('natural-bag', 'Natural', '740 g') +
            lineupCell('chocolate-bag', 'Chocolate', '930 g') +
            lineupCell('vanilla-bag', 'Vanilla', '700 g') +
          '</div>' +
          '<p style="padding:16px 20px;margin:0;color:var(--ink-soft);font-size:14px">Single-serve 20-pouch boxes are available in Natural and Chocolate.</p>' +
        '</aside>' +
      '</div>';
  }

  function lineupCell(id, name, size) {
    return '<figure>' +
      '<img src="' + esc((CFG.images || {})[id]) + '" alt="ARM Protein ' + esc(name) + ' Almond bag" loading="lazy">' +
      '<figcaption><div style="font-weight:700">' + esc(name) + '</div>' +
      '<div class="tabular" style="font-size:11px;color:var(--ink-faint)">' + esc(size) + '</div></figcaption>' +
      '</figure>';
  }

  function header(active) {
    var s = state.session || {};
    var links = s.role === 'admin'
      ? [['admin', 'Approval queue', 'Queue']]
      : [['order', 'New order', 'Order'], ['history', 'Order history', 'History']];

    var nav = links.map(function (l) {
      return '<button class="navbtn" data-go="' + l[0] + '"' +
        (active === l[0] ? ' aria-current="page"' : '') + '>' +
        '<span class="only-sm">' + esc(l[2]) + '</span>' +
        '<span class="not-sm">' + esc(l[1]) + '</span></button>';
    }).join('');

    return '<div class="bar"><div class="bar-in">' +
      '<div style="display:flex;align-items:baseline;gap:8px"><span class="mark">ARM</span>' +
      '<span class="kick not-sm">Wholesale</span></div>' +
      '<nav style="display:flex;gap:2px;margin-left:6px">' + nav + '</nav>' +
      '<div class="right"><span class="who">' + esc(s.storeName || '') + '</span>' +
      '<button class="btn btn-ghost" id="signout">Sign out</button></div>' +
      '</div></div>';
  }

  function viewOrder() {
    var s = state.session;
    var q = currentQuote();
    var shipTo = state.altShipTo || s.shipTo || {};

    var rows = (s.products || []).map(function (p) {
      var qty = state.quantities[p.id] || 0;
      var rate = _rateForKind(s.profile, p.kind);
      var line = null;
      for (var i = 0; i < q.lines.length; i++) if (q.lines[i].productId === p.id) line = q.lines[i];
      var partCase = p.caseSize > 1 && qty > 0 && qty % p.caseSize !== 0;

      // Sold out: keep the flavour on the page so they can see it is a stock
      // gap rather than a discontinued line, but take away the controls.
      if (p.available === false) {
        return '<div class="row prow prow-off">' +
          '<img class="thumb" src="' + esc((CFG.images || {})[p.id]) + '" alt="" loading="lazy">' +
          '<div class="pinfo">' +
            '<div class="pname">' + esc(p.flavour) + '</div>' +
            '<div class="pmeta">' + esc(p.label) + '</div>' +
          '</div>' +
          '<div class="pctl">' +
            '<div class="offnote">' + esc(p.note || 'Not available right now') + '</div>' +
          '</div>' +
        '</div>';
      }

      return '<div class="row prow">' +
        '<img class="thumb" src="' + esc((CFG.images || {})[p.id]) + '" alt="" loading="lazy">' +
        '<div class="pinfo">' +
          '<div class="pname">' + esc(p.flavour) + '</div>' +
          '<div class="pmeta">' + esc(p.label) + ' · ' + esc((CFG.blurbs || {})[p.id] || '') + '</div>' +
          (partCase ? '<div class="warn-inline">Ships in cases of ' + p.caseSize + '</div>' : '') +
        '</div>' +
        '<div class="pctl">' +
          '<div class="rate">' + money(rate) +
            '<span class="only-sm" style="font-size:11px;color:var(--ink-faint)"> ea</span>' +
            '<small class="not-sm">per unit</small></div>' +
          '<div class="stepper">' +
            '<button type="button" class="btn btn-quiet" data-step="' + p.id + '" data-by="-' + p.caseSize + '"' +
              (qty === 0 ? ' disabled' : '') + ' aria-label="Remove ' + p.caseSize + ' ' + esc(p.flavour) + '">&minus;</button>' +
            '<label class="sr" for="q-' + p.id + '">' + esc(p.flavour) + ' ' + esc(p.label) + ' quantity</label>' +
            '<input id="q-' + p.id + '" class="field tabular qty" inputmode="numeric" data-qty="' + p.id + '" ' +
              'value="' + (qty === 0 ? '' : qty) + '" placeholder="0">' +
            '<button type="button" class="btn btn-quiet" data-step="' + p.id + '" data-by="' + p.caseSize + '"' +
              ' aria-label="Add ' + p.caseSize + ' ' + esc(p.flavour) + '">+</button>' +
          '</div>' +
          '<div class="amt tabular">' + (qty > 0 ? money(line ? line.amount : 0) : '<span style="color:var(--ink-faint)">—</span>') + '</div>' +
        '</div>' +
      '</div>';
    }).join('');

    var f = state.form;

    return header('order') +
      '<main>' +
        '<div class="page-head">' +
          '<div><h1>New order</h1><p class="sub">' + esc(s.storeName) + '</p></div>' +
          '<span class="pill">' + esc(s.categoryLabel) + ' pricing</span>' +
        '</div>' +

        (state.copiedFrom
          ? '<div class="note-ok" style="margin-top:22px">Quantities copied from <strong class="tabular">' +
            esc(state.copiedFrom) + '</strong>. Everything is priced at today&rsquo;s rates — check the amounts and add a delivery date before sending.' +
            (state.copyDropped
              ? ' <strong>' + esc(state.copyDropped) + '</strong> was left out — not available right now.'
              : '') +
            '</div>'
          : '') +

        '<form id="order-form" class="grid-pad" novalidate>' +
          '<div>' +
            '<h2 class="lbl">Products</h2>' +
            '<div class="panel">' + rows + '</div>' +
            '<p style="margin:8px 0 0;font-size:13px;color:var(--ink-faint)">Bags ship in cases of 6. Boxes ship individually.</p>' +

            '<h2 class="lbl" style="margin-top:36px">Delivery details</h2>' +
            '<div class="fields">' +
              '<div><label class="lbl" for="deliveryDate">Requested delivery date</label>' +
                '<input id="deliveryDate" class="field tabular" type="date" min="' + todayISO() + '" data-form="deliveryDate" value="' + esc(f.deliveryDate || '') + '"></div>' +
              '<div><label class="lbl" for="poNumber">Your PO number <span style="text-transform:none;letter-spacing:normal">(optional)</span></label>' +
                '<input id="poNumber" class="field" data-form="poNumber" value="' + esc(f.poNumber || '') + '" placeholder="PO-4417"></div>' +
              '<div><label class="lbl" for="contactName">Placed by</label>' +
                '<input id="contactName" class="field" data-form="contactName" value="' + esc(f.contactName || '') + '" placeholder="Name" autocomplete="name"></div>' +
              '<div><label class="lbl" for="contactEmail">Email for order updates</label>' +
                '<input id="contactEmail" class="field" type="email" data-form="contactEmail" value="' + esc(f.contactEmail || '') + '" placeholder="orders@store.ca" autocomplete="email"></div>' +
              '<div class="span2"><label class="lbl" for="notes">Anything we should know <span style="text-transform:none;letter-spacing:normal">(optional)</span></label>' +
                '<textarea id="notes" class="field" rows="3" data-form="notes" placeholder="Receiving hours, dock instructions, a date you need it by…">' + esc(f.notes || '') + '</textarea></div>' +
            '</div>' +

            '<h2 class="lbl" style="margin-top:36px">Ship to</h2>' +
            '<div class="panel" style="padding:16px">' + shipToBlock(shipTo) + '</div>' +
            '<p style="margin:8px 0 0;font-size:13px;color:var(--ink-faint)">Freight is calculated from the province you are shipping to.</p>' +
          '</div>' +

          '<aside class="summary"><div class="panel" style="overflow:hidden">' +
            '<div style="border-bottom:1px solid var(--rule);padding:12px 16px"><span class="kick" style="color:var(--ink-soft)">Order summary</span></div>' +
            '<div style="padding:10px 16px">' + summaryLines(q) + '</div>' +
            '<div class="tabular" style="border-top:1px solid var(--rule);padding:10px 16px">' +
              sumLine('Subtotal', money(q.subtotal)) +
              sumLine('Freight<span style="display:block;font-size:11px;color:var(--ink-faint)">' + esc(q.shippingReason) + '</span>', money(q.shipping)) +
              sumLine('GST 5%', money(q.gst)) +
              '<div class="sum-total"><span style="font-family:Nunito,sans-serif;font-weight:700">Total</span>' +
              '<span style="font-size:18px;font-weight:700">' + money(q.total) + '</span></div>' +
              '<p style="text-align:right;margin:4px 0 0;font-size:11px;color:var(--ink-faint);font-family:Nunito,sans-serif">' +
                q.units + (q.units === 1 ? ' unit' : ' units') + '</p>' +
            '</div>' +
            (q.belowMinimum
              ? '<div class="note-warn" style="border-top:1px solid var(--rule)">' +
                '<span class="kick" style="color:var(--caution)">Minimum order</span>' +
                '<p style="margin:6px 0 0;font-size:13px">Wholesale orders start at ' + q.minOrderUnits +
                ' units. Add <strong>' + q.shortBy + ' more</strong> to send this one.</p></div>'
              : '') +
            (q.warnings.length
              ? '<div class="note-warn" style="border-top:1px solid var(--rule)"><span class="kick" style="color:var(--caution)">Check before sending</span><ul style="margin:8px 0 0;padding-left:18px;font-size:13px">' +
                q.warnings.map(function (w) { return '<li>' + esc(w) + '</li>'; }).join('') + '</ul></div>'
              : '') +
            '<div style="border-top:1px solid var(--rule);padding:16px">' +
              (state.error ? '<p class="alert alert-err" style="margin:0 0 12px" role="alert">' + esc(state.error) + '</p>' : '') +
              '<button type="submit" class="btn btn-primary" style="width:100%"' +
                (state.busy || q.isEmpty || q.belowMinimum ? ' disabled' : '') + '>' +
                (state.busy ? 'Sending order'
                  : q.belowMinimum ? 'Add ' + q.shortBy + ' more to send'
                  : 'Send order for approval') + '</button>' +
              '<p style="text-align:center;margin:8px 0 0;font-size:13px;color:var(--ink-faint)">We confirm every order before it ships. Nothing is charged here.</p>' +
            '</div>' +
          '</div></aside>' +

          '<div class="mobile-bar"><div class="mobile-in">' +
            '<div class="tabular" style="flex:1;min-width:0">' +
              '<div style="font-size:11px;color:var(--ink-faint)">' + q.units + (q.units === 1 ? ' unit' : ' units') + ' · incl. GST</div>' +
              '<div style="font-size:18px;font-weight:700">' + money(q.total) + '</div></div>' +
            '<button type="submit" class="btn btn-primary"' +
              (state.busy || q.isEmpty || q.belowMinimum ? ' disabled' : '') + '>' +
              (state.busy ? 'Sending' : q.belowMinimum ? 'Add ' + q.shortBy : 'Send order') + '</button>' +
          '</div></div>' +
        '</form>' +
      '</main>';
  }

  function sumLine(label, value) {
    return '<div class="sum-line"><span style="font-family:Nunito,sans-serif;color:var(--ink-soft)">' + label + '</span><span>' + value + '</span></div>';
  }

  function summaryLines(q) {
    if (q.isEmpty) {
      return '<p style="padding:22px 0;text-align:center;color:var(--ink-faint);font-size:14px">Nothing added yet. Set a quantity to start building the order.</p>';
    }
    return '<ul class="tabular" style="list-style:none;margin:0;padding:0;font-size:14px">' +
      q.lines.map(function (l) {
        return '<li class="row" style="display:flex;gap:12px;padding:8px 0">' +
          '<span style="width:30px;color:var(--ink-faint)">' + l.qty + '&times;</span>' +
          '<span style="flex:1;min-width:0;font-family:Nunito,sans-serif;color:var(--ink-soft)">' + esc(l.description) + '</span>' +
          '<span>' + money(l.amount) + '</span></li>';
      }).join('') + '</ul>';
  }

  function shipToBlock(a) {
    if (state.altShipTo) {
      return '<div class="fields">' +
        '<div class="span2"><label class="lbl" for="a-name">Location name</label><input id="a-name" class="field" data-alt="name" value="' + esc(a.name || '') + '"></div>' +
        '<div class="span2"><label class="lbl" for="a-line1">Street address</label><input id="a-line1" class="field" data-alt="line1" value="' + esc(a.line1 || '') + '"></div>' +
        '<div><label class="lbl" for="a-city">City</label><input id="a-city" class="field" data-alt="city" value="' + esc(a.city || '') + '"></div>' +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">' +
          '<div><label class="lbl" for="a-prov">Province</label><select id="a-prov" class="field tabular" data-alt="province">' +
            ['', 'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'].map(function (p) {
              return '<option value="' + p + '"' + (a.province === p ? ' selected' : '') + '>' + (p || '—') + '</option>';
            }).join('') + '</select></div>' +
          '<div><label class="lbl" for="a-postal">Postal code</label><input id="a-postal" class="field tabular" data-alt="postal" value="' + esc(a.postal || '') + '"></div>' +
        '</div>' +
        '<div class="span2"><button type="button" class="btn btn-quiet" id="use-default">Use our address on file</button></div>' +
      '</div>';
    }
    return '<div style="display:flex;flex-wrap:wrap;gap:16px;justify-content:space-between">' +
      '<address style="font-style:normal">' +
        '<div style="font-weight:700">' + esc(a.name || '') + '</div>' +
        '<div style="color:var(--ink-soft)">' + esc([a.line1, a.line2].filter(Boolean).join(', ')) + '</div>' +
        '<div style="color:var(--ink-soft)">' + esc([a.city, a.province, a.postal].filter(Boolean).join(', ')) + '</div>' +
      '</address>' +
      '<button type="button" class="btn btn-quiet" id="use-alt">Ship somewhere else</button></div>';
  }

  function viewHistory() {
    var body;
    if (state.orders === null) {
      body = '<div class="panel" style="margin-top:22px">' +
        [0, 1, 2].map(function () {
          return '<div class="row" style="padding:16px"><div class="skel" style="height:16px;width:180px"></div>' +
            '<div class="skel" style="height:12px;width:240px;margin-top:8px"></div></div>';
        }).join('') + '</div>';
    } else if (!state.orders.length) {
      body = '<div class="panel empty" style="margin-top:22px"><p style="font-weight:700;color:var(--ink)">No orders yet</p>' +
        '<p>Once you send your first order it will appear here with its status and invoice.</p>' +
        '<button class="btn btn-primary" style="margin-top:20px" data-go="order">Start an order</button></div>';
    } else {
      body = '<div class="panel" style="margin-top:22px">' + state.orders.map(function (o) {
        return '<div class="row listrow">' +
          '<button class="navbtn" style="flex:1;min-width:0;text-align:left;color:var(--ink);padding:0" data-open="' + esc(o.id) + '">' +
            '<div class="tabular" style="font-weight:700">' + esc(o.id) + '</div>' +
            '<div style="font-size:13px;color:var(--ink-faint);font-weight:400">' + esc(when(o.submittedAt)) + ' · ' + o.units + ' units' +
            (o.invoiceNumber ? ' · ' + esc(o.invoiceNumber) : '') + '</div></button>' +
          '<span class="tabular" style="font-weight:700">' + money(o.total) + '</span>' +
          statusBadge(o.status) +
          '<button class="btn btn-quiet" style="padding:6px 12px;font-size:14px" data-reorder="' + esc(o.id) + '">Order again</button>' +
        '</div>';
      }).join('') + '</div>';
    }

    return header('history') + '<main><h1>Order history</h1>' +
      (state.error ? '<p class="alert alert-err">' + esc(state.error) + '</p>' : '') + body + '</main>';
  }

  function orderLines(o) {
    return '<ul class="tabular" style="list-style:none;margin:0;padding:0;font-size:14px">' +
      (o.lines || []).map(function (l) {
        return '<li class="row" style="display:flex;gap:12px;padding:8px 0">' +
          '<span style="width:30px;color:var(--ink-faint)">' + l.qty + '&times;</span>' +
          '<span style="flex:1;min-width:0;font-family:Nunito,sans-serif;color:var(--ink-soft)">' + esc(l.description) + '</span>' +
          '<span style="width:88px;text-align:right">' + money(l.amount) + '</span></li>';
      }).join('') + '</ul>' +
      '<div class="tabular" style="border-top:1px solid var(--rule);margin-top:12px;padding-top:12px;font-size:14px">' +
        sumLine('Subtotal', money(o.subtotal)) + sumLine('Freight', money(o.shipping)) + sumLine('GST 5%', money(o.gst)) +
        '<div class="sum-total"><span style="font-family:Nunito,sans-serif;font-weight:700">Total</span><span style="font-weight:700">' + money(o.total) + '</span></div>' +
      '</div>';
  }

  function viewDetail() {
    if (state.order === null) {
      return header(state.session.role === 'admin' ? 'admin' : 'history') +
        '<main><div class="skel" style="height:32px;width:200px"></div>' +
        '<div class="panel" style="margin-top:28px;padding:16px">' +
        '<div class="skel" style="height:16px;width:100%;margin-bottom:10px"></div>' +
        '<div class="skel" style="height:16px;width:70%"></div></div></main>';
    }
    if (state.order === false) {
      return header(state.session.role === 'admin' ? 'admin' : 'history') +
        '<main><h1>We could not find that</h1>' +
        '<p class="sub">The order may belong to another store, or the link is out of date.</p>' +
        '<button class="btn btn-primary" style="margin-top:20px" data-go="' + (state.session.role === 'admin' ? 'admin' : 'history') + '">Back</button></main>';
    }

    var o = state.order;
    var a = o.shipTo || {};
    return header(state.session.role === 'admin' ? 'admin' : 'history') +
      '<main style="max-width:760px">' +
        (state.copiedFrom === o.id ? '' : '') +
        (state.justSubmittedId === o.id ? '<div class="note-ok" style="margin-bottom:22px">Order received. We review every order before it ships — you will get the invoice by email once it is approved.</div>' : '') +
        '<div class="page-head"><div><h1 class="tabular">' + esc(o.id) + '</h1>' +
          '<p class="sub" style="font-size:14px">' + esc(o.storeName) + ' · submitted ' + esc(when(o.submittedAt)) + '</p></div>' +
          statusBadge(o.status) + '</div>' +

        (state.session.role === 'admin' && o.shipLink && !o.tracking
          ? '<div class="panel" style="margin-top:20px;padding:14px 16px">' +
            '<h2 class="lbl" style="margin:0 0 6px">Warehouse tracking link</h2>' +
            '<p style="margin:0 0 10px;font-size:13px;color:var(--ink-faint)">' +
              'Send this if their invoice email went astray. It opens the tracking form for this order only.</p>' +
            '<input class="field tabular" readonly value="' + esc(o.shipLink) + '" ' +
              'onfocus="this.select()" style="font-size:12px"></div>'
          : '') +

        (o.tracking
          ? '<div class="note-ok" style="display:block;margin-top:20px">' +
            '<p style="margin:0;font-weight:700">On its way</p>' +
            '<p class="tabular" style="margin:6px 0 0;font-size:15px">' +
              esc(o.carrier) + ' &middot; ' + esc(o.tracking) + '</p>' +
            (o.trackingUrl ? '<p style="margin:8px 0 0"><a href="' + esc(o.trackingUrl) +
              '" target="_blank" rel="noopener"><strong>Track this shipment</strong></a></p>' : '') +
            '</div>'
          : '') +

        (String(o.status).toLowerCase() !== 'rejected' && o.invoiceNumber
          ? '<p class="panel" style="margin-top:20px;padding:12px 16px;font-size:14px">Invoiced as <strong class="tabular">' + esc(o.invoiceNumber) + '</strong>. ' +
            (o.pdfUrl ? '<a href="' + esc(o.pdfUrl) + '" target="_blank" rel="noopener"><strong>Open the invoice PDF</strong></a>' : '') + '</p>'
          : '') +

        (o.status === 'rejected'
          ? '<div style="margin-top:20px;background:var(--danger-soft);color:var(--danger);border-radius:var(--r-panel);padding:12px 16px;font-size:14px">' +
            '<p style="margin:0;font-weight:700">This order was not processed.</p>' +
            (o.decisionNote ? '<p style="margin:4px 0 0">' + esc(o.decisionNote) + '</p>' : '') +
            '<button class="btn btn-quiet" style="margin-top:12px" data-reorder="' + esc(o.id) + '">Place a revised order</button></div>'
          : '') +

        '<div class="panel" style="margin-top:28px;padding:16px">' + orderLines(o) + '</div>' +

        '<div style="display:grid;gap:24px;margin-top:28px;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))">' +
          '<div><h2 class="lbl">Ship to</h2><address style="font-style:normal;font-size:14px;color:var(--ink-soft)">' +
            '<div style="font-weight:700;color:var(--ink)">' + esc(a.name || '') + '</div>' +
            '<div>' + esc([a.line1, a.line2].filter(Boolean).join(', ')) + '</div>' +
            '<div>' + esc([a.city, a.province, a.postal].filter(Boolean).join(', ')) + '</div></address></div>' +
          '<div><h2 class="lbl">Requested delivery</h2><p class="tabular" style="font-size:14px;margin:0">' + esc(o.deliveryDate || 'Not specified') + '</p>' +
            (o.poNumber ? '<h2 class="lbl" style="margin-top:16px">Your PO</h2><p class="tabular" style="font-size:14px;margin:0">' + esc(o.poNumber) + '</p>' : '') + '</div>' +
        '</div>' +

        (o.notes ? '<div style="margin-top:28px"><h2 class="lbl">Notes sent with this order</h2><p style="font-size:14px;color:var(--ink-soft)">' + esc(o.notes) + '</p></div>' : '') +

        // Approving from here, not just from the queue: the email links straight
        // to one order, so the action has to be where it lands you.
        (state.session.role === 'admin' && String(o.status).toLowerCase() === 'pending'
          ? (state.rejecting === o.id
              ? '<div class="panel" style="margin-top:32px;padding:16px">' +
                  '<label class="lbl" for="dnote">Why is this going back?</label>' +
                  '<textarea id="dnote" class="field" rows="3" data-note="1" ' +
                    'placeholder="We are out of Vanilla until the 22nd - resubmit with Natural?">' +
                    esc(state.note) + '</textarea>' +
                  '<p style="font-size:13px;color:var(--ink-faint);margin:8px 0 12px">' +
                    'The store gets this wording, and can send a revised order.</p>' +
                  '<div style="display:flex;flex-wrap:wrap;gap:12px">' +
                    '<button class="btn btn-danger" data-decide="reject" data-id="' + esc(o.id) + '"' +
                      (state.busy ? ' disabled' : '') + '>' +
                      (state.busy ? 'Sending' : 'Decline and notify') + '</button>' +
                    '<button class="btn btn-quiet" data-cancel="1"' + (state.busy ? ' disabled' : '') + '>Cancel</button>' +
                  '</div></div>'
              : '<div style="display:flex;flex-wrap:wrap;gap:12px;margin-top:40px">' +
                  '<button class="btn btn-primary" data-decide="approve" data-id="' + esc(o.id) + '"' +
                    (state.busy ? ' disabled' : '') + '>' +
                    (state.busy ? 'Generating invoice' : 'Approve and invoice') + '</button>' +
                  '<button class="btn btn-quiet" data-reject="' + esc(o.id) + '"' +
                    (state.busy ? ' disabled' : '') + '>Decline</button>' +
                  '<button class="btn btn-quiet" data-go="admin">Back to the queue</button>' +
                '</div>')
          : '<div style="display:flex;flex-wrap:wrap;gap:12px;margin-top:40px">' +
              '<button class="btn btn-quiet" data-go="' + (state.session.role === 'admin' ? 'admin' : 'history') + '">Back</button>' +
              (state.session.role !== 'admin' ? '<button class="btn btn-primary" data-reorder="' + esc(o.id) + '">Order these again</button>' : '') +
            '</div>') +
      '</main>';
  }

  function shipHeader(sub) {
    return '<div class="bar"><div class="bar-in">' +
      '<span class="mark">ARM</span>' +
      '<span class="kick" style="color:var(--sand)">' + esc(sub) + '</span>' +
      '</div></div>';
  }

  function shipAddress(a) {
    a = a || {};
    return '<address style="font-style:normal;font-size:15px;line-height:1.6;color:var(--ink)">' +
      (a.name ? '<strong>' + esc(a.name) + '</strong><br>' : '') +
      esc([a.line1, a.line2].filter(Boolean).join(', ')) + '<br>' +
      esc([a.city, a.province, a.postal].filter(Boolean).join(', ')) + '</address>';
  }

  function viewShip() {
    if (state.shipOrder === null) {
      return shipHeader('Shipping') + '<main><div class="panel" style="padding:16px">' +
        '<div class="skel" style="height:20px;width:240px"></div>' +
        '<div class="skel" style="height:14px;width:180px;margin-top:12px"></div></div></main>';
    }
    if (state.shipOrder === false) {
      return shipHeader('Shipping') + '<main><div class="panel empty" style="margin-top:22px">' +
        '<p style="font-weight:700;color:var(--ink)">' + esc(state.error || 'That link no longer works.') + '</p>' +
        '<p>Ask ARM to resend it.</p></div></main>';
    }

    var o = state.shipOrder;
    var done = String(o.status).toLowerCase() === 'shipped';

    var items = (o.lines || []).map(function (l) {
      return '<div class="row" style="display:flex;gap:14px;padding:11px 0;font-size:15px">' +
        '<span class="tabular" style="width:44px;color:var(--ink-faint)">' + l.qty + '&times;</span>' +
        '<span style="flex:1;min-width:0">' + esc(l.description) + '</span></div>';
    }).join('');

    var form = done
      ? '<div class="note-ok" style="display:block;margin-top:24px">' +
          '<p style="margin:0;font-weight:700">Tracking recorded. The store has been emailed.</p>' +
          '<p class="tabular" style="margin:8px 0 0;font-size:15px">' +
            esc(o.carrier) + ' &middot; ' + esc(o.tracking) + '</p>' +
          (state.shipDone && state.shipDone.trackingUrl
            ? '<p style="margin:10px 0 0"><a href="' + esc(state.shipDone.trackingUrl) +
              '" target="_blank" rel="noopener"><strong>Open the tracking page</strong></a></p>' : '') +
        '</div>'
      : '<div class="panel" style="margin-top:24px;padding:18px">' +
          '<label class="lbl" for="carrier">Carrier</label>' +
          '<select id="carrier" class="field" data-ship="carrier" style="height:48px">' +
            '<option value="">Choose one</option>' +
            (state.shipCarriers || []).map(function (c) {
              return '<option value="' + esc(c) + '"' +
                (state.shipForm.carrier === c ? ' selected' : '') + '>' + esc(c) + '</option>';
            }).join('') +
          '</select>' +
          '<label class="lbl" for="tracking" style="margin-top:18px">Tracking number</label>' +
          '<input id="tracking" class="field tabular" data-ship="tracking" style="height:48px" ' +
            'autocapitalize="characters" spellcheck="false" placeholder="e.g. 1234 5678 9012" ' +
            'value="' + esc(state.shipForm.tracking) + '">' +
          (state.error ? '<p class="alert alert-err" role="alert">' + esc(state.error) + '</p>' : '') +
          '<button class="btn btn-primary" data-ship-submit="1" style="width:100%;margin-top:20px;height:52px"' +
            (state.busy ? ' disabled' : '') + '>' +
            (state.busy ? 'Saving' : 'Save and notify the store') + '</button>' +
          '<p style="text-align:center;margin:10px 0 0;font-size:13px;color:var(--ink-faint)">' +
            'The store is emailed the tracking straight away.</p>' +
        '</div>';

    return shipHeader('Shipping') + '<main>' +
      '<div class="page-head"><h1>' + esc(o.storeName) + '</h1>' +
        '<p class="tabular" style="font-size:14px;color:var(--ink-soft)">' +
          esc(o.invoiceNumber || o.id) + ' &middot; ' + (Number(o.units) || 0) + ' units</p></div>' +

      '<div style="display:grid;gap:24px;margin-top:24px;grid-template-columns:repeat(auto-fit,minmax(240px,1fr))">' +
        '<div><h2 class="lbl">Ship to</h2>' + shipAddress(o.shipTo) + '</div>' +
        '<div><h2 class="lbl">Requested delivery</h2>' +
          '<p class="tabular" style="font-size:15px;margin:0">' + esc(o.deliveryDate || 'Not specified') + '</p>' +
          (o.poNumber ? '<h2 class="lbl" style="margin-top:16px">Their PO</h2>' +
            '<p class="tabular" style="font-size:15px;margin:0">' + esc(o.poNumber) + '</p>' : '') +
        '</div>' +
      '</div>' +

      '<h2 class="lbl" style="margin-top:28px">Pack</h2>' +
      '<div class="panel" style="padding:4px 16px">' + items + '</div>' +
      (o.notes ? '<p style="margin-top:16px;font-size:14px;color:var(--ink-soft)">' +
        '<strong style="color:var(--ink)">From the store:</strong> ' + esc(o.notes) + '</p>' : '') +

      form +

      '<p style="margin-top:32px"><a href="#/ships/' + esc(state.shipToken) + '">' +
        '<strong>Everything still to ship</strong></a></p>' +
      '</main>';
  }

  function viewShipQueue() {
    var body;
    if (state.shipQueue === null) {
      body = '<div class="panel" style="margin-top:22px;padding:16px">' +
        '<div class="skel" style="height:18px;width:220px"></div></div>';
    } else if (!state.shipQueue.length) {
      body = '<div class="panel empty" style="margin-top:22px">' +
        '<p style="font-weight:700;color:var(--ink)">Nothing waiting</p>' +
        '<p>Approved orders appear here until tracking is added.</p></div>';
    } else {
      body = '<div class="panel" style="margin-top:22px">' + state.shipQueue.map(function (o) {
        var a = o.shipTo || {};
        return '<div class="row listrow">' +
          '<div style="flex:1;min-width:0">' +
            '<div style="font-weight:700">' + esc(o.storeName) + '</div>' +
            '<div class="tabular" style="font-size:13px;color:var(--ink-faint)">' +
              esc(o.invoiceNumber || o.id) + ' &middot; ' + o.units + ' units &middot; ' +
              esc(a.city || '') + '</div>' +
          '</div>' +
          '<a class="btn btn-quiet" href="#/ship/' + esc(o.token) + '">Add tracking</a>' +
        '</div>';
      }).join('') + '</div>';
    }

    return shipHeader('Shipping') + '<main>' +
      '<div class="page-head"><h1>Still to ship</h1>' +
        (state.shipQueue && state.shipQueue.length
          ? '<p class="tabular" style="font-size:14px;color:var(--ink-soft)">' +
            state.shipQueue.length + ' order' + (state.shipQueue.length === 1 ? '' : 's') + '</p>' : '') +
      '</div>' +
      (state.error ? '<p class="alert alert-err">' + esc(state.error) + '</p>' : '') +
      body + '</main>';
  }

  function viewAdmin() {
    var notices = state.notices.map(function (n, i) {
      return '<li style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;border-radius:var(--r-panel);padding:12px 16px;font-size:14px;' +
        (n.kind === 'approved' ? 'background:var(--sand-soft);color:var(--brand)' : 'background:var(--danger-soft);color:var(--danger)') + '">' +
        (n.kind === 'approved'
          ? '<span><strong>' + esc(n.storeName) + '</strong> approved as <strong class="tabular">' + esc(n.invoiceNumber) + '</strong>. Invoice sent to you and the warehouse.</span>' +
            (n.pdfUrl ? '<a href="' + esc(n.pdfUrl) + '" target="_blank" rel="noopener"><strong>Open PDF</strong></a>' : '')
          : '<span><strong>' + esc(n.storeName) + '</strong> declined. The store has been notified.</span>') +
        '<button class="navbtn" style="margin-left:auto;color:inherit;padding:2px 6px" data-dismiss="' + i + '" aria-label="Dismiss">&times;</button></li>';
    }).join('');

    var body;
    if (state.queue === null) {
      body = '<div class="panel" style="margin-top:22px;padding:16px">' +
        '<div class="skel" style="height:18px;width:220px"></div>' +
        '<div class="skel" style="height:14px;width:320px;margin-top:10px"></div>' +
        '<div class="skel" style="height:40px;width:200px;margin-top:20px"></div></div>';
    } else if (!state.queue.pending.length) {
      body = '<div class="panel empty" style="margin-top:22px"><p style="font-weight:700;color:var(--ink)">Nothing waiting</p>' +
        '<p>New orders from your retail accounts land here for approval. You will get an email the moment one arrives.</p></div>';
    } else {
      body = '<div style="display:grid;gap:20px;margin-top:22px">' + state.queue.pending.map(orderCard).join('') + '</div>';
    }

    var decided = (state.queue && state.queue.decided && state.queue.decided.length)
      ? '<div class="panel" style="margin-top:22px">' + state.queue.decided.map(function (o) {
          return '<div class="row listrow">' +
            '<button class="navbtn" style="flex:1;min-width:0;text-align:left;color:var(--ink);padding:0" data-open="' + esc(o.id) + '">' +
              '<div style="font-weight:700">' + esc(o.storeName) + '</div>' +
              '<div class="tabular" style="font-size:13px;color:var(--ink-faint);font-weight:400">' + esc(o.id) + ' · ' + esc(when(o.decidedAt || o.submittedAt)) + '</div></button>' +
            (o.invoiceNumber ? '<span class="tabular" style="font-size:14px;color:var(--ink-soft)">' + esc(o.invoiceNumber) + '</span>' : '') +
            '<span class="tabular" style="font-weight:700">' + money(o.total) + '</span>' + statusBadge(o.status) +
            (o.pdfUrl ? '<a href="' + esc(o.pdfUrl) + '" target="_blank" rel="noopener" style="font-size:14px"><strong>Invoice</strong></a>' : '') +
          '</div>';
        }).join('') + '</div>'
      : '<p class="panel" style="margin-top:22px;padding:20px;font-size:14px;color:var(--ink-faint)">Approved and declined orders will be listed here.</p>';

    var waiting = state.queue && state.queue.pending.length
      ? '<p class="tabular" style="font-size:14px;color:var(--ink-soft)">' + state.queue.pending.length + ' waiting · ' +
        money(state.queue.pending.reduce(function (t, o) { return t + Number(o.total || 0); }, 0)) + ' incl. GST</p>'
      : '';

    return header('admin') + '<main>' +
      '<div class="page-head"><h1>Approval queue</h1>' + waiting + '</div>' +
      (notices ? '<ul style="list-style:none;margin:22px 0 0;padding:0;display:grid;gap:8px">' + notices + '</ul>' : '') +
      (state.error ? '<p class="alert alert-err">' + esc(state.error) + '</p>' : '') +
      body +
      '<h2 class="lbl" style="margin-top:48px">Recently decided</h2>' + decided +
      '</main>';
  }

  function orderCard(o) {
    var a = o.shipTo || {};
    var busy = state.busy && state.rejecting !== o.id;
    var actions;

    if (state.rejecting === o.id) {
      actions = '<label class="lbl" for="note-' + esc(o.id) + '">What should the store change?</label>' +
        '<textarea id="note-' + esc(o.id) + '" class="field" rows="2" data-note="1" placeholder="We\'re out of Vanilla until the 22nd — resubmit with Natural?">' + esc(state.note) + '</textarea>' +
        '<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:12px">' +
          '<button class="btn btn-danger" data-decide="reject" data-id="' + esc(o.id) + '"' + (state.busy ? ' disabled' : '') + '>' +
            (state.busy ? 'Sending' : 'Decline and notify') + '</button>' +
          '<button class="btn btn-quiet" data-cancel="1"' + (state.busy ? ' disabled' : '') + '>Cancel</button>' +
        '</div>';
    } else {
      actions = '<div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px">' +
        '<button class="btn btn-primary" data-decide="approve" data-id="' + esc(o.id) + '"' + (busy ? ' disabled' : '') + '>' +
          (busy ? 'Generating invoice' : 'Approve and invoice') + '</button>' +
        '<button class="btn btn-quiet" data-reject="' + esc(o.id) + '"' + (busy ? ' disabled' : '') + '>Decline</button>' +
        '<span style="font-size:13px;color:var(--ink-faint)">Approving emails the invoice to you and the warehouse.</span>' +
      '</div>';
    }

    return '<article class="panel" style="overflow:hidden">' +
      '<div style="display:flex;flex-wrap:wrap;justify-content:space-between;gap:4px 24px;border-bottom:1px solid var(--rule);padding:12px 16px">' +
        '<div><h3 style="font-size:16px">' + esc(o.storeName) + '</h3>' +
          '<p class="tabular" style="margin:0;font-size:13px;color:var(--ink-faint)">' + esc(o.id) + ' · ' + esc(when(o.submittedAt)) +
          (o.poNumber ? ' · PO ' + esc(o.poNumber) : '') + '</p></div>' +
        '<div class="tabular" style="text-align:right"><div style="font-size:18px;font-weight:700">' + money(o.total) + '</div>' +
          '<div style="font-size:11px;color:var(--ink-faint)">' + o.units + ' units · incl. GST</div></div>' +
      '</div>' +
      '<div class="card-grid">' +
        '<div>' + orderLines(o) + '</div>' +
        '<div style="display:grid;gap:16px;align-content:start;font-size:14px">' +
          '<div><div class="lbl">Ship to</div><address style="font-style:normal;color:var(--ink-soft)">' +
            '<div style="font-weight:700;color:var(--ink)">' + esc(a.name || '') + '</div>' +
            '<div>' + esc([a.line1, a.line2].filter(Boolean).join(', ')) + '</div>' +
            '<div>' + esc([a.city, a.province, a.postal].filter(Boolean).join(', ')) + '</div></address></div>' +
          '<div><div class="lbl">Requested delivery</div><div class="tabular">' + esc(o.deliveryDate || 'Not specified') + '</div></div>' +
          (o.contactName || o.contactEmail
            ? '<div><div class="lbl">Placed by</div><div>' + esc(o.contactName || '—') + '</div>' +
              (o.contactEmail ? '<a href="mailto:' + esc(o.contactEmail) + '">' + esc(o.contactEmail) + '</a>' : '') + '</div>'
            : '') +
          (o.notes ? '<div><div class="lbl">Notes</div><p style="margin:0;color:var(--ink-soft)">' + esc(o.notes) + '</p></div>' : '') +
        '</div>' +
      '</div>' +
      ((o.warnings && o.warnings.length)
        ? '<div class="note-warn" style="border-top:1px solid var(--rule)"><span class="kick" style="color:var(--caution)">Worth a look</span>' +
          '<ul style="margin:6px 0 0;padding-left:18px;font-size:13px">' + o.warnings.map(function (w) { return '<li>' + esc(w) + '</li>'; }).join('') + '</ul></div>'
        : '') +
      '<div style="border-top:1px solid var(--rule);padding:12px 16px">' + actions +
        (state.error && state.rejecting === o.id ? '<p class="alert alert-err" role="alert">' + esc(state.error) + '</p>' : '') +
      '</div></article>';
  }

  // ── render + events ───────────────────────────────────────────
  function render() {
    var html;
    if (state.view === 'ship') html = viewShip();
    else if (state.view === 'ships') html = viewShipQueue();
    else if (!state.session) html = viewSignIn();
    else if (state.view === 'order') html = viewOrder();
    else if (state.view === 'history') html = viewHistory();
    else if (state.view === 'detail') html = viewDetail();
    else if (state.view === 'admin') html = viewAdmin();
    else html = viewOrder();

    app.innerHTML = html;

    var code = document.getElementById('code');
    if (code) code.focus();
  }

  function setQty(id, value) {
    var next = Math.max(0, Math.min(999, value));
    state.quantities[id] = next;
    render();
  }

  document.addEventListener('click', function (ev) {
    var ship = ev.target.closest && ev.target.closest('[data-ship-submit]');
    if (ship) { ev.preventDefault(); return submitShip(); }

    var t = ev.target.closest('[data-go],[data-step],[data-open],[data-reorder],[data-decide],[data-reject],[data-cancel],[data-dismiss],#signout,#use-alt,#use-default');
    if (!t) return;

    if (t.id === 'signout') return signOut();

    if (t.id === 'use-alt') {
      state.altShipTo = Object.assign({}, state.session.shipTo);
      return render();
    }
    if (t.id === 'use-default') { state.altShipTo = null; return render(); }

    if (t.hasAttribute('data-go')) return go(t.getAttribute('data-go'));
    if (t.hasAttribute('data-open')) return go('detail', { id: t.getAttribute('data-open') });
    if (t.hasAttribute('data-dismiss')) {
      state.notices.splice(Number(t.getAttribute('data-dismiss')), 1);
      return render();
    }

    if (t.hasAttribute('data-step')) {
      var id = t.getAttribute('data-step');
      return setQty(id, (state.quantities[id] || 0) + Number(t.getAttribute('data-by')));
    }

    if (t.hasAttribute('data-reorder')) return reorder(t.getAttribute('data-reorder'));

    if (t.hasAttribute('data-reject')) {
      state.rejecting = t.getAttribute('data-reject');
      state.note = '';
      state.error = '';
      return render();
    }
    if (t.hasAttribute('data-cancel')) {
      state.rejecting = '';
      state.note = '';
      return render();
    }
    if (t.hasAttribute('data-decide')) {
      return decide(t.getAttribute('data-id'), t.getAttribute('data-decide'));
    }
  });

  document.addEventListener('input', function (ev) {
    var el = ev.target;
    if (el.hasAttribute('data-qty')) {
      var v = parseInt(el.value.replace(/\D/g, ''), 10) || 0;
      state.quantities[el.getAttribute('data-qty')] = Math.max(0, Math.min(999, v));
      // Repaint totals without stealing the caret from the field being typed in.
      var q = currentQuote();
      var pos = el.selectionStart;
      render();
      var again = document.querySelector('[data-qty="' + el.getAttribute('data-qty') + '"]');
      if (again) { again.focus(); try { again.setSelectionRange(pos, pos); } catch (e) {} }
      return;
    }
    if (el.hasAttribute('data-ship')) {
      state.shipForm[el.getAttribute('data-ship')] = el.value;
      return;                      // no repaint: it would steal the caret
    }
    if (el.hasAttribute('data-form')) { state.form[el.getAttribute('data-form')] = el.value; return; }
    if (el.hasAttribute('data-alt')) {
      state.altShipTo = state.altShipTo || {};
      state.altShipTo[el.getAttribute('data-alt')] = el.value;
      if (el.getAttribute('data-alt') === 'province') render();
      return;
    }
    if (el.hasAttribute('data-note')) { state.note = el.value; return; }
  });

  document.addEventListener('change', function (ev) {
    var el = ev.target;
    if (el.hasAttribute('data-ship')) state.shipForm[el.getAttribute('data-ship')] = el.value;
  });

  document.addEventListener('submit', function (ev) {
    ev.preventDefault();
    if (ev.target.id === 'signin-form') return signIn();
    if (ev.target.id === 'order-form') return submitOrder();
  });

  window.addEventListener('hashchange', function () {
    var r = routeFromHash();
    if (r && (r.view === 'ship' || r.view === 'ships')) {
      state.shipToken = r.token;
      state.view = r.view;
      state.error = '';
      state.shipDone = null;
      render();
      if (r.view === 'ship') loadShipOrder(); else loadShipQueue();
      return;
    }
    if (!state.session || !r) return;
    if (r.view === 'detail') { state.view = 'detail'; render(); loadOrder(r.id); }
    else go(r.view);
  });

  // ── actions ───────────────────────────────────────────────────
  function signIn() {
    var input = document.getElementById('code');
    var code = input ? input.value : '';
    if (!code.trim() || state.busy) return;

    state.busy = true;
    state.error = '';
    render();

    api('portalSignIn', { code: code }).then(function (d) {
      state.busy = false;
      state.token = d.token;
      try { localStorage.setItem(TOKEN_KEY, d.token); } catch (e) {}
      state.session = d;
      state.form.contactEmail = d.contactEmail || '';

      // Arriving from an emailed link, land on what the link pointed at rather
      // than dumping everyone on their default view. The admin queue stays
      // admin-only; an order id is safe either way because the server scopes it.
      var want = routeFromHash();
      if (want && (want.view !== 'admin' || d.role === 'admin')) {
        if (want.view === 'detail') { state.view = 'detail'; render(); loadOrder(want.id); return; }
        return go(want.view);
      }
      go(d.role === 'admin' ? 'admin' : 'order');
    }, function (e) {
      state.busy = false;
      state.error = e.message;
      render();
    });
  }

  function submitOrder() {
    var q = currentQuote();
    if (state.busy || !q || q.isEmpty || q.belowMinimum) return;

    if (state.altShipTo && (!state.altShipTo.line1 || !state.altShipTo.city || !state.altShipTo.province)) {
      state.error = 'Fill in the delivery street, city and province.';
      return render();
    }

    state.busy = true;
    state.error = '';
    render();

    api('portalSubmit', {
      order: {
        quantities: state.quantities,
        deliveryDate: state.form.deliveryDate || '',
        poNumber: state.form.poNumber || '',
        contactName: state.form.contactName || '',
        contactEmail: state.form.contactEmail || '',
        notes: state.form.notes || '',
        shipTo: state.altShipTo || null
      }
    }).then(function (d) {
      state.busy = false;
      state.quantities = {};
      state.form = { contactEmail: state.session.contactEmail || '' };
      state.altShipTo = null;
      state.copiedFrom = '';
      state.copyDropped = '';
      state.justSubmittedId = d.id;      // shows the confirmation on that order
      go('detail', { id: d.id });
    }, function (e) {
      state.busy = false;
      state.error = e.message;
      render();
    });
  }

  function reorder(id) {
    api('portalOrder', { id: id }).then(function (d) {
      var o = d.order;
      if (!o) { state.error = 'That order is no longer available.'; return render(); }

      // Quantities only — priced at today's rates, no stale delivery date.
      var known = {};
      (state.session.products || []).forEach(function (p) { known[p.id] = p; });
      var next = {}, dropped = [];
      (o.lines || []).forEach(function (l) {
        var p = l && l.productId ? known[l.productId] : null;
        if (!p) return;
        if (p.available === false) { dropped.push(p.flavour + ' ' + p.label); return; }
        next[l.productId] = Math.min(Number(l.qty) || 0, 999);
      });

      state.quantities = next;
      state.copyDropped = dropped.join(' and ');
      state.copiedFrom = o.id;
      state.form = { contactEmail: state.session.contactEmail || '' };
      state.altShipTo = null;
      go('order');
    }, function (e) { state.error = e.message; render(); });
  }

  function decide(id, decision) {
    if (state.busy) return;

    // Reachable from the queue or from a single order opened by an emailed
    // link, and in that second case the queue was never loaded.
    var order = null;
    (((state.queue && state.queue.pending) || [])).forEach(function (o) {
      if (o.id === id) order = o;
    });
    if (!order && state.order && state.order.id === id) order = state.order;

    state.busy = true;
    state.error = '';
    render();

    api('portalDecide', { id: id, decision: decision, note: decision === 'reject' ? state.note : '' })
      .then(function (d) {
        state.busy = false;
        state.rejecting = '';
        state.note = '';
        state.notices.unshift({
          kind: decision === 'approve' ? 'approved' : 'rejected',
          storeName: order ? order.storeName : id,
          invoiceNumber: d.invoiceNumber,
          pdfUrl: d.pdfUrl
        });

        // Deciding from a single order leaves you looking at that order, so
        // refresh it in place; the queue behind it reloads either way.
        if (state.view === 'detail') loadOrder(id);
        loadQueue();
      }, function (e) {
        state.busy = false;
        state.error = e.message;
        render();
      });
  }

  // ── boot ──────────────────────────────────────────────────────
  var booted = routeFromHash();
  if (booted && (booted.view === 'ship' || booted.view === 'ships')) {
    state.shipToken = booted.token;
    state.view = booted.view;
    render();
    if (booted.view === 'ship') loadShipOrder(); else loadShipQueue();
    return;
  }

  try { state.token = localStorage.getItem(TOKEN_KEY); } catch (e) {}

  if (!state.token) {
    render();
  } else {
    api('portalSession').then(function (d) {
      state.session = d;
      state.form.contactEmail = d.contactEmail || '';
      var r = routeFromHash();
      var view = r ? r.view : (d.role === 'admin' ? 'admin' : 'order');
      if (r && r.view === 'detail') { state.view = 'detail'; render(); loadOrder(r.id); }
      else go(view);
    }, function () {
      try { localStorage.removeItem(TOKEN_KEY); } catch (e) {}
      state.token = null;
      render();
    });
  }
})();
